import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import {
  FaFileContract,
  FaSync,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUser,
  FaBriefcase,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUmbrellaBeach,
  FaClock,
  FaUserTie,
  FaUsers,
  FaCheckCircle,
  FaUserSlash,
  FaExclamationTriangle,
  FaPlus,
  FaEdit,
  FaBan,
  FaPrint,
} from "react-icons/fa";
import { genericGet, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import "./ContractTrackSheet.css";

const formatTableDate = (value) => {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return value;
  }
};

const formatCell = (value, isDate = false) => {
  if (value === null || value === undefined || value === "") return "—";
  if (isDate) return formatTableDate(value);
  return String(value).trim();
};

const formatMoney = (value, decimals = 2) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return formatCell(value);
  return amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};

const getStaffCode = (row) => row?.staff_code || row?.staff_id || "";

const getContractId = (row) => row?.contract_details?.id || row?.contract_details?.contract_id || null;

const toInputDate = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const EMPTY_CREATE_CONTRACT_FORM = {
  staff_code: "",
  start_date: "",
  end_date: "",
  contract_type: "",
  contracted_salary: "",
  annual_leave_days: "",
  total_gratuity_taken: "0",
};

const EMPTY_EDIT_CONTRACT_FORM = {
  staff_code: "",
  start_date: "",
  end_date: "",
  contracted_salary: "",
  annual_leave_days: "",
};

const getContractValue = (row, field) => {
  const contract = row?.contract_details || {};
  if (field === "contract_type") return contract.contract_type;
  if (field === "contract_start_date") return contract.start_date;
  if (field === "contract_end_date") return contract.end_date;
  return row?.[field];
};

const formatContractStatus = (status) => {
  if (status === 10001 || status === "10001") return "Active";
  if (status === 10002 || status === "10002") return "Inactive";
  return formatCell(status);
};

const hasContract = (row) => {
  const contract = row?.contract_details;
  if (!contract || typeof contract !== "object") return false;
  return Boolean(contract.id || contract.start_date || contract.contract_type);
};

const isActiveContract = (row) => {
  if (!hasContract(row)) return false;
  const status = row.contract_details?.status;
  return status === 10001 || status === "10001";
};

const isExpiringContract = (row) => {
  if (!hasContract(row)) return false;
  const contract = row.contract_details;
  const expiresAfter = Number(contract.contract_expires_after);
  if (!Number.isNaN(expiresAfter) && expiresAfter >= 0 && expiresAfter < 10) {
    return true;
  }
  if (!contract.end_date) return false;
  const end = new Date(contract.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const daysRemaining = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return daysRemaining >= 0 && daysRemaining < 10;
};

const STAT_FILTERS = {
  totalActiveStaff: {
    label: "Total Active Staff",
    match: () => true,
  },
  activeContracts: {
    label: "Active Contracts",
    match: isActiveContract,
  },
  noContract: {
    label: "No Contract",
    match: (row) => !hasContract(row),
  },
  expiringContracts: {
    label: "Expiring Contracts",
    match: isExpiringContract,
  },
};

const getRowsByStatFilter = (data, filterKey) => {
  const rows = Array.isArray(data) ? data : [];
  if (!filterKey || !STAT_FILTERS[filterKey]) return rows;
  return rows.filter(STAT_FILTERS[filterKey].match);
};

const getStatFilterCount = (data, filterKey) => getRowsByStatFilter(data, filterKey).length;

const StatCard = ({ label, value, icon: Icon, accent, bg, isActive, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    className="h-100 contract-tracksheet-stat-card"
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
    style={{
      background: bg,
      border: isActive ? `2px solid ${accent}` : `1px solid ${accent}22`,
      borderLeft: `4px solid ${accent}`,
      borderRadius: "10px",
      padding: "0.65rem 0.85rem",
      boxShadow: isActive
        ? `0 4px 14px ${accent}33`
        : "0 2px 8px rgba(15, 23, 42, 0.06)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      transform: isActive ? "translateY(-1px)" : "none",
      outline: "none",
    }}
  >
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div>
        <div style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, lineHeight: 1.2 }}>
          {label}
        </div>
        <div style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
          {value}
        </div>
      </div>
      <div
        style={{
          width: "34px",
          height: "34px",
          borderRadius: "8px",
          background: `${accent}18`,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={15} />
      </div>
    </div>
  </div>
);

const ContractTrackSheet = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("staff_id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  const [showContractFormModal, setShowContractFormModal] = useState(false);
  const [contractFormMode, setContractFormMode] = useState("create");
  const [contractForm, setContractForm] = useState(EMPTY_CREATE_CONTRACT_FORM);
  const [contractTypes, setContractTypes] = useState([]);
  const [isLoadingContractTypes, setIsLoadingContractTypes] = useState(false);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [selectedStaffOption, setSelectedStaffOption] = useState(null);
  const [selectedContractTypeOption, setSelectedContractTypeOption] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isDeactivatingContract, setIsDeactivatingContract] = useState(false);

  const fetchTrackSheet = useCallback(() => {
    setIsLoading(true);
    genericGet(ENDPOINTS.CONTRACT_TRACKSHEET)
      .then((res) => {
        setIsLoading(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setRows(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          cogoToast.error(res?.data?.message || "Failed to load contract tracksheet.");
          setRows([]);
        }
      })
      .catch(() => {
        setIsLoading(false);
        cogoToast.error("Failed to load contract tracksheet.");
        setRows([]);
      });
  }, []);

  useEffect(() => {
    fetchTrackSheet();
  }, [fetchTrackSheet]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="ms-1 opacity-50" size={11} />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ms-1" size={11} />
    ) : (
      <FaSortDown className="ms-1" size={11} />
    );
  };

  const getSortableValue = (row, field) => {
    const value = getContractValue(row, field);
    if (field === "contract_start_date" || field === "contract_end_date" || field === "joined_at") {
      return formatCell(value, true).toLowerCase();
    }
    return formatCell(value).toLowerCase();
  };

  const openContractDetails = (row) => {
    setSelectedRow(row);
    setShowDetailsModal(true);
  };

  const closeContractDetails = () => {
    if (isDeactivatingContract) return;
    setShowDetailsModal(false);
    setSelectedRow(null);
    setShowDeactivateModal(false);
  };

  const openDeactivateModal = () => {
    const contractId = getContractId(selectedRow);
    if (!contractId) {
      cogoToast.error("Contract ID is missing. Cannot deactivate this contract.");
      return;
    }
    setShowDeactivateModal(true);
  };

  const closeDeactivateModal = () => {
    if (isDeactivatingContract) return;
    setShowDeactivateModal(false);
  };

  const handleDeactivateContract = async () => {
    const contractId = getContractId(selectedRow);
    if (!contractId) {
      cogoToast.error("Contract ID is missing.");
      return;
    }

    setIsDeactivatingContract(true);
    try {
      const res = await genericPost(ENDPOINTS.STAFF_CONTRACTS_DEACTIVATE, {
        contract_id: contractId,
      });
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        cogoToast.success(res?.data?.message || "Contract deactivated successfully.");
        setShowDeactivateModal(false);
        closeContractDetails();
        fetchTrackSheet();
      } else {
        cogoToast.error(res?.data?.message || "Failed to deactivate contract.");
      }
    } catch {
      cogoToast.error("Failed to deactivate contract.");
    } finally {
      setIsDeactivatingContract(false);
    }
  };

  const loadContractTypes = useCallback(async () => {
    setIsLoadingContractTypes(true);
    try {
      const res = await genericGet(ENDPOINTS.STAFF_CONTRACTS_CONTRACT_TYPES);
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        setContractTypes(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        cogoToast.error(res?.data?.message || "Failed to load contract types.");
        setContractTypes([]);
      }
    } catch {
      cogoToast.error("Failed to load contract types.");
      setContractTypes([]);
    } finally {
      setIsLoadingContractTypes(false);
    }
  }, []);

  const staffOptions = useMemo(
    () =>
      rows
        .map((row) => {
          const staffCode = getStaffCode(row);
          if (!staffCode) return null;
          return {
            value: staffCode,
            label: `${formatCell(row.staff_name)} (${staffCode})`,
            row,
          };
        })
        .filter(Boolean),
    [rows]
  );

  const contractTypeOptions = useMemo(
    () =>
      contractTypes.map((type) => ({
        value: type.name,
        label: type.name,
        description: type.description,
      })),
    [contractTypes]
  );

  const openCreateContractModal = (row = null) => {
    const staffCode = row ? getStaffCode(row) : "";
    const preselectedStaff = staffCode
      ? staffOptions.find((option) => option.value === staffCode) || null
      : null;

    setContractFormMode("create");
    setContractForm({
      ...EMPTY_CREATE_CONTRACT_FORM,
      staff_code: staffCode,
    });
    setSelectedStaffOption(preselectedStaff);
    setSelectedContractTypeOption(null);
    setShowContractFormModal(true);
    setShowDetailsModal(false);
    if (contractTypes.length === 0) {
      loadContractTypes();
    }
  };

  const openEditContractModal = (row) => {
    const staffCode = getStaffCode(row);
    const contractDetails = row?.contract_details || {};
    if (!staffCode) {
      cogoToast.error("Staff code is missing. Cannot edit contract.");
      return;
    }
    if (!hasContract(row)) {
      cogoToast.error("No contract found for this staff member.");
      return;
    }

    setContractFormMode("edit");
    setContractForm({
      staff_code: staffCode,
      start_date: toInputDate(contractDetails.start_date),
      end_date: toInputDate(contractDetails.end_date),
      contracted_salary:
        contractDetails.contracted_salary === null || contractDetails.contracted_salary === undefined
          ? ""
          : String(contractDetails.contracted_salary),
      annual_leave_days:
        contractDetails.annual_leave_days === null || contractDetails.annual_leave_days === undefined
          ? ""
          : String(contractDetails.annual_leave_days),
    });
    setSelectedStaffOption(null);
    setSelectedContractTypeOption(null);
    setSelectedRow(row);
    setShowContractFormModal(true);
    setShowDetailsModal(false);
  };

  const closeContractFormModal = () => {
    if (isSavingContract) return;
    setShowContractFormModal(false);
    setContractForm(EMPTY_CREATE_CONTRACT_FORM);
    setSelectedStaffOption(null);
    setSelectedContractTypeOption(null);
  };

  const handleContractFormChange = (field, value) => {
    setContractForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateContract = async (e) => {
    e?.preventDefault();
    const staffCode = contractForm.staff_code?.trim();
    if (!staffCode) {
      cogoToast.error("Please select a staff member.");
      return;
    }
    if (!contractForm.start_date || !contractForm.end_date) {
      cogoToast.error("Start date and end date are required.");
      return;
    }
    if (!contractForm.contract_type?.trim()) {
      cogoToast.error("Please select a contract type.");
      return;
    }
    if (contractForm.contracted_salary === "" || contractForm.annual_leave_days === "") {
      cogoToast.error("Salary and annual leave days are required.");
      return;
    }

    setIsSavingContract(true);
    try {
      const res = await genericPost(ENDPOINTS.STAFF_CONTRACTS_CREATE, {
        staff_code: staffCode,
        start_date: contractForm.start_date,
        end_date: contractForm.end_date,
        contract_type: contractForm.contract_type,
        contracted_salary: Number(contractForm.contracted_salary),
        annual_leave_days: Number(contractForm.annual_leave_days),
        total_gratuity_taken: Number(contractForm.total_gratuity_taken || 0),
      });
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        cogoToast.success(res?.data?.message || "Contract created successfully.");
        closeContractFormModal();
        fetchTrackSheet();
      } else {
        cogoToast.error(res?.data?.message || "Failed to create contract.");
      }
    } catch {
      cogoToast.error("Failed to create contract.");
    } finally {
      setIsSavingContract(false);
    }
  };

  const handleUpdateContract = async (e) => {
    e?.preventDefault();
    const staffCode = contractForm.staff_code?.trim();
    if (!staffCode) {
      cogoToast.error("Staff code is missing.");
      return;
    }
    if (!contractForm.start_date || !contractForm.end_date) {
      cogoToast.error("Start date and end date are required.");
      return;
    }
    if (contractForm.contracted_salary === "" || contractForm.annual_leave_days === "") {
      cogoToast.error("Salary and annual leave days are required.");
      return;
    }

    setIsSavingContract(true);
    try {
      const res = await genericPost(ENDPOINTS.STAFF_CONTRACTS_UPDATE, {
        staff_code: staffCode,
        start_date: contractForm.start_date,
        end_date: contractForm.end_date,
        contracted_salary: Number(contractForm.contracted_salary),
        annual_leave_days: Number(contractForm.annual_leave_days),
      });
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        cogoToast.success(res?.data?.message || "Contract updated successfully.");
        closeContractFormModal();
        fetchTrackSheet();
      } else {
        cogoToast.error(res?.data?.message || "Failed to update contract.");
      }
    } catch {
      cogoToast.error("Failed to update contract.");
    } finally {
      setIsSavingContract(false);
    }
  };

  const handleStatCardClick = (filterKey) => {
    setActiveStatFilter((current) => (current === filterKey ? null : filterKey));
  };

  const handlePrint = () => {
    window.print();
  };

  const printedAtLabel = useMemo(
    () =>
      new Date().toLocaleString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const filteredSortedRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    let data = getRowsByStatFilter(rows, activeStatFilter);

    if (query) {
      data = data.filter((row) => {
        const contract = row?.contract_details || {};
        const haystack = [
          row?.staff_id,
          row?.staff_name,
          row?.email,
          row?.gender,
          row?.telephone,
          row?.designation,
          row?.office,
          row?.department_name,
          row?.sector_name,
          contract?.contract_type,
          contract?.start_date,
          contract?.end_date,
          row?.joined_at,
        ]
          .map((value) => formatCell(value))
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    data.sort((a, b) => {
      const aVal = getSortableValue(a, sortField);
      const bVal = getSortableValue(b, sortField);
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return data;
  }, [rows, search, sortField, sortDirection, activeStatFilter]);

  const summaryStats = useMemo(() => {
    const data = Array.isArray(rows) ? rows : [];
    return {
      totalActiveStaff: getStatFilterCount(data, "totalActiveStaff"),
      activeContracts: getStatFilterCount(data, "activeContracts"),
      noContract: getStatFilterCount(data, "noContract"),
      expiringContracts: getStatFilterCount(data, "expiringContracts"),
    };
  }, [rows]);

  const tableHeaderBaseStyle = {
    fontSize: "12pt",
    fontWeight: 600,
    color: "#475569",
    padding: "0.55rem 0.65rem",
    whiteSpace: "nowrap",
  };

  const sortableHeaderStyle = {
    ...tableHeaderBaseStyle,
    cursor: "pointer",
    userSelect: "none",
  };

  const cellStyle = {
    padding: "0.55rem 0.65rem",
    verticalAlign: "middle",
    fontSize: "10pt",
  };

  const headerActionButtonStyle = {
    borderRadius: "8px",
    fontSize: "0.88rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
  };

  const contract = selectedRow?.contract_details || {};

  const DetailItem = ({ label, value, icon: Icon }) => (
    <div
      className="h-100 p-3 rounded"
      style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
    >
      <div className="d-flex align-items-center gap-2 mb-1 text-muted" style={{ fontSize: "0.78rem" }}>
        {Icon ? <Icon size={12} /> : null}
        <span>{label}</span>
      </div>
      <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "#1e293b" }}>
        {value ?? "—"}
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-3 contract-tracksheet-page">
      <div className="contract-tracksheet-print-header">
        <div className="contract-tracksheet-print-header__top">
          <div className="contract-tracksheet-print-header__brand">
            <img
              src="/images/irs_logo.png"
              alt="Islamic Relief Somalia"
              className="contract-tracksheet-print-header__logo"
            />
            <div>
              <div className="contract-tracksheet-print-header__org">Islamic Relief Somalia</div>
              <div style={{ fontSize: "9pt", color: "#64748b" }}>Human Resources — Payroll</div>
            </div>
          </div>
          <div className="contract-tracksheet-print-header__meta">
            <div>
              <strong>Printed:</strong> {printedAtLabel}
            </div>
            <div>
              <strong>Records:</strong> {filteredSortedRows.length}
            </div>
          </div>
        </div>
        <h1 className="contract-tracksheet-print-header__title">Contract TrackSheet</h1>
        <p className="contract-tracksheet-print-header__subtitle">
          Staff contract lifecycle report
        </p>
      </div>

      <div className="contract-tracksheet-print-stats">
        <div className="contract-tracksheet-print-stat">
          <div className="contract-tracksheet-print-stat__label">Total Active Staff</div>
          <div className="contract-tracksheet-print-stat__value">{summaryStats.totalActiveStaff}</div>
        </div>
        <div className="contract-tracksheet-print-stat">
          <div className="contract-tracksheet-print-stat__label">Active Contracts</div>
          <div className="contract-tracksheet-print-stat__value">{summaryStats.activeContracts}</div>
        </div>
        <div className="contract-tracksheet-print-stat">
          <div className="contract-tracksheet-print-stat__label">No Contract</div>
          <div className="contract-tracksheet-print-stat__value">{summaryStats.noContract}</div>
        </div>
        <div className="contract-tracksheet-print-stat">
          <div className="contract-tracksheet-print-stat__label">Expiring Soon</div>
          <div className="contract-tracksheet-print-stat__value">{summaryStats.expiringContracts}</div>
        </div>
      </div>

      {(activeStatFilter || search.trim()) && (
        <div className="contract-tracksheet-print-filter">
          {activeStatFilter && (
            <span>
              Filter: <strong>{STAT_FILTERS[activeStatFilter].label}</strong>
              {search.trim() ? " · " : ""}
            </span>
          )}
          {search.trim() && (
            <span>
              Search: <strong>{search.trim()}</strong>
            </span>
          )}
        </div>
      )}

      <div
        className="mb-3 p-3 rounded contract-tracksheet-screen-hero"
        style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
          color: "white",
          boxShadow: "0 8px 24px rgba(37, 99, 235, 0.25)",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div style={{ flex: "1 1 280px" }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <FaFileContract size={22} />
              <h3 className="mb-0" style={{ fontWeight: 700 }}>
                Contract TrackSheet
              </h3>
            </div>
            <div style={{ opacity: 0.9, fontSize: "0.9rem" }}>
              Monitor staff contract details and track contract lifecycle in one place.
            </div>
          </div>
          <div
            className="d-flex flex-wrap align-items-center gap-2"
            style={{ flex: "1 1 320px", justifyContent: "flex-end" }}
          >
            <div className="position-relative no-print" style={{ minWidth: "220px", maxWidth: "320px", flex: "1 1 220px" }}>
              <FaSearch
                className="position-absolute"
                style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748b" }}
              />
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search staff, office, contract..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  borderRadius: "8px",
                  border: "none",
                  fontSize: "0.88rem",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              />
            </div>
            <button
              type="button"
              className="btn d-flex align-items-center gap-2 no-print"
              onClick={handlePrint}
              disabled={isLoading || filteredSortedRows.length === 0}
              style={headerActionButtonStyle}
            >
              <FaPrint />
              Print
            </button>
            <button
              type="button"
              className="btn d-flex align-items-center gap-2 no-print"
              onClick={() => openCreateContractModal()}
              style={headerActionButtonStyle}
            >
              <FaPlus />
              New Contract
            </button>
            <button
              type="button"
              className="btn d-flex align-items-center gap-2 no-print"
              onClick={fetchTrackSheet}
              disabled={isLoading}
              style={{
                ...headerActionButtonStyle,
                opacity: isLoading ? 0.75 : 1,
              }}
            >
              {isLoading ? <Spinner size="sm" color="dark" /> : <FaSync />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      <Row className="g-2 mb-3 contract-tracksheet-screen-stats">
        <Col xs={6} md={3}>
          <StatCard
            label="Total Active Staff"
            value={summaryStats.totalActiveStaff}
            icon={FaUsers}
            accent="#2563eb"
            bg="#f8fbff"
            isActive={activeStatFilter === "totalActiveStaff"}
            onClick={() => handleStatCardClick("totalActiveStaff")}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatCard
            label="Active Contracts"
            value={summaryStats.activeContracts}
            icon={FaCheckCircle}
            accent="#16a34a"
            bg="#f7fdf9"
            isActive={activeStatFilter === "activeContracts"}
            onClick={() => handleStatCardClick("activeContracts")}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatCard
            label="No Contract"
            value={summaryStats.noContract}
            icon={FaUserSlash}
            accent="#64748b"
            bg="#f8fafc"
            isActive={activeStatFilter === "noContract"}
            onClick={() => handleStatCardClick("noContract")}
          />
        </Col>
        <Col xs={6} md={3}>
          <StatCard
            label="Expiring Contracts"
            value={summaryStats.expiringContracts}
            icon={FaExclamationTriangle}
            accent="#ea580c"
            bg="#fffaf5"
            isActive={activeStatFilter === "expiringContracts"}
            onClick={() => handleStatCardClick("expiringContracts")}
          />
        </Col>
      </Row>

      <div
        className="card border-0 shadow-sm contract-tracksheet-print-card"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        {activeStatFilter && (
          <div
            className="px-3 py-2 d-flex flex-wrap align-items-center justify-content-between gap-2 no-print"
            style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: "0.85rem" }}
          >
            <span style={{ color: "#475569" }}>
              Showing <strong>{STAT_FILTERS[activeStatFilter].label}</strong>{" "}
              ({filteredSortedRows.length} record{filteredSortedRows.length === 1 ? "" : "s"})
            </span>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setActiveStatFilter(null)}
              style={{ textDecoration: "none", fontWeight: 600 }}
            >
              Clear filter
            </button>
          </div>
        )}
        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="text-muted mt-2">Loading contract tracksheet...</div>
            </div>
          ) : filteredSortedRows.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {rows.length === 0
                ? "No contract tracksheet records found."
                : activeStatFilter
                  ? `No records match "${STAT_FILTERS[activeStatFilter].label}".`
                  : "No records match your search."}
            </div>
          ) : (
            <div className="table-responsive contract-tracksheet-table">
              <table className="table table-hover mb-0">
                <thead style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <tr>
                    <th style={{ ...tableHeaderBaseStyle, width: "52px" }}>#</th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("staff_id")}>
                      Staff ID {renderSortIcon("staff_id")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("staff_name")}>
                      Staff Name {renderSortIcon("staff_name")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("designation")}>
                      Designation {renderSortIcon("designation")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("office")}>
                      Office {renderSortIcon("office")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("department_name")}>
                      Department {renderSortIcon("department_name")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("contract_type")}>
                      Type {renderSortIcon("contract_type")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("contract_start_date")}>
                      Start Date {renderSortIcon("contract_start_date")}
                    </th>
                    <th style={sortableHeaderStyle} onClick={() => handleSort("contract_end_date")}>
                      End Date {renderSortIcon("contract_end_date")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSortedRows.map((row, index) => {
                    const isExpiring = isExpiringContract(row);
                    return (
                    <tr
                      key={row.id || row.staff_id || index}
                      className={`contract-tracksheet-row${isExpiring ? " contract-tracksheet-row-expiring" : ""}`}
                      style={
                        isExpiring
                          ? undefined
                          : { backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc" }
                      }
                      onClick={() => openContractDetails(row)}
                      title={isExpiring ? "Contract expiring soon — click to view details" : "Click to view contract details"}
                    >
                      <td style={{ ...cellStyle, color: "#64748b" }}>{index + 1}</td>
                      <td style={cellStyle}>{formatCell(row.staff_id)}</td>
                      <td style={cellStyle}>{formatCell(row.staff_name)}</td>
                      <td style={cellStyle}>{formatCell(row.designation)}</td>
                      <td style={cellStyle}>{formatCell(row.office)}</td>
                      <td style={cellStyle}>{formatCell(row.department_name)}</td>
                      <td style={cellStyle}>{formatCell(getContractValue(row, "contract_type"))}</td>
                      <td style={cellStyle}>{formatCell(getContractValue(row, "contract_start_date"), true)}</td>
                      <td style={cellStyle}>{formatCell(getContractValue(row, "contract_end_date"), true)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="contract-tracksheet-print-footer">
        Islamic Relief Somalia — Contract TrackSheet — {printedAtLabel} — {filteredSortedRows.length} record
        {filteredSortedRows.length === 1 ? "" : "s"}
      </div>

      <Modal isOpen={showDetailsModal} toggle={closeContractDetails} size="lg" centered className="no-print">
        <ModalHeader
          toggle={closeContractDetails}
          style={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
            color: "white",
            borderBottom: "none",
          }}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={closeContractDetails}
            />
          }
        >
          <div className="d-flex align-items-center gap-2">
            <FaFileContract />
            <span>Contract Details</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem" }}>
          {selectedRow ? (
            <>
              <div
                className="p-3 rounded mb-3"
                style={{
                  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  border: "1px solid #cbd5e1",
                }}
              >
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <FaUser style={{ color: "#2563eb" }} />
                      <h5 className="mb-0" style={{ fontWeight: 700, color: "#1e293b" }}>
                        {formatCell(selectedRow.staff_name)}
                      </h5>
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.88rem" }}>
                      {formatCell(selectedRow.staff_id)} · {formatCell(selectedRow.designation)} · {formatCell(selectedRow.office)}
                    </div>
                  </div>
                  <Badge color="primary" pill style={{ fontSize: "0.8rem", padding: "0.45rem 0.75rem" }}>
                    {formatCell(contract.contract_type)}
                  </Badge>
                </div>
              </div>

              {!contract || Object.keys(contract).length === 0 ? (
                <div className="text-center text-muted py-4">No contract details available for this staff member.</div>
              ) : (
                <>
                  <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: "#334155", fontWeight: 700 }}>
                    <FaBriefcase style={{ color: "#2563eb" }} />
                    Contract Information
                  </h6>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <DetailItem
                        label="Contract Type"
                        value={formatCell(contract.contract_type)}
                        icon={FaBriefcase}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="Start Date"
                        value={formatCell(contract.start_date, true)}
                        icon={FaCalendarAlt}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="End Date"
                        value={formatCell(contract.end_date, true)}
                        icon={FaCalendarAlt}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="Contracted Salary"
                        value={formatMoney(contract.contracted_salary)}
                        icon={FaMoneyBillWave}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="Annual Leave Days"
                        value={formatCell(contract.annual_leave_days)}
                        icon={FaUmbrellaBeach}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="Expires After (Days)"
                        value={formatMoney(contract.contract_expires_after,0)}
                        icon={FaClock}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailItem
                        label="Status"
                        value={formatContractStatus(contract.status)}
                        icon={FaFileContract}
                      />
                    </Col>
                    {/* <Col md={4}>
                      <DetailItem
                        label="Total Gratuity Taken"
                        value={formatMoney(contract.total_gratuity_taken)}
                        icon={FaMoneyBillWave}
                      />
                    </Col> */}
                    <Col md={4}>
                      <DetailItem
                        label="Staff Payroll Total"
                        value={formatMoney(contract.staff_payroll_total)}
                        icon={FaMoneyBillWave}
                      />
                    </Col>
                  </Row>

                  <h6 className="mb-3 d-flex align-items-center gap-2" style={{ color: "#334155", fontWeight: 700 }}>
                    <FaUserTie style={{ color: "#2563eb" }} />
                    Record Information
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <DetailItem
                        label="Created By"
                        value={formatCell(contract.created_by_name) + ' ' + formatCell(contract.created_by_email)}
                        icon={FaUserTie}
                      />
                    </Col>
                   
                    <Col md={6}>
                      <DetailItem
                        label="Created At"
                        value={formatCell(contract.created_at, true)}
                        icon={FaClock}
                      />
                    </Col>
                    <Col md={6}>
                      <DetailItem
                        label="Updated At"
                        value={formatCell(contract.updated_at, true)}
                        icon={FaClock}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #e2e8f0" }}>
          {selectedRow && hasContract(selectedRow) ? (
            <>
              <Button
                color="primary"
                onClick={() => openEditContractModal(selectedRow)}
                className="d-flex align-items-center gap-2"
                style={{ borderRadius: "8px" }}
              >
                <FaEdit size={13} />
                Edit Contract
              </Button>
              {isActiveContract(selectedRow) && getContractId(selectedRow) ? (
                <Button
                  color="danger"
                  onClick={openDeactivateModal}
                  className="d-flex align-items-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <FaBan size={12} />
                  Deactivate Contract
                </Button>
              ) : null}
            </>
          ) : selectedRow ? (
            <Button
              color="primary"
              onClick={() => openCreateContractModal(selectedRow)}
              className="d-flex align-items-center gap-2"
              style={{ borderRadius: "8px" }}
            >
              <FaPlus size={12} />
              Add Contract
            </Button>
          ) : null}
          <Button color="secondary" onClick={closeContractDetails} style={{ borderRadius: "8px" }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showDeactivateModal} toggle={closeDeactivateModal} centered className="no-print">
        <ModalHeader toggle={closeDeactivateModal}>Deactivate Contract</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Are you sure you want to deactivate the contract for{" "}
            <strong>{formatCell(selectedRow?.staff_name)}</strong> ({formatCell(getStaffCode(selectedRow))})?
          </p>
          <p className="text-danger mb-0" style={{ fontSize: "0.88rem", fontWeight: 600 }}>
            Deactivating this contract cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeactivateModal} disabled={isDeactivatingContract}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeactivateContract} disabled={isDeactivatingContract}>
            {isDeactivatingContract ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deactivating...
              </>
            ) : (
              "Deactivate Contract"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showContractFormModal} toggle={closeContractFormModal} size="lg" centered className="no-print">
        <ModalHeader toggle={closeContractFormModal}>
          {contractFormMode === "create" ? "Create New Contract" : "Edit Contract"}
        </ModalHeader>
        <Form onSubmit={contractFormMode === "create" ? handleCreateContract : handleUpdateContract}>
          <ModalBody>
            {contractFormMode === "create" ? (
              <>
                <FormGroup>
                  <Label>
                    Staff <span className="text-danger">*</span>
                  </Label>
                  <Select
                    isSearchable
                    isClearable
                    placeholder={staffOptions.length === 0 ? "No staff available" : "Search and select staff..."}
                    options={staffOptions}
                    value={selectedStaffOption}
                    onChange={(option) => {
                      setSelectedStaffOption(option);
                      handleContractFormChange("staff_code", option?.value || "");
                    }}
                    isDisabled={isSavingContract}
                    noOptionsMessage={() => "No staff found"}
                    styles={{
                      control: (base) => ({ ...base, borderRadius: "8px", minHeight: "38px" }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Start Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={contractForm.start_date}
                        onChange={(e) => handleContractFormChange("start_date", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        End Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={contractForm.end_date}
                        onChange={(e) => handleContractFormChange("end_date", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label>
                    Contract Type <span className="text-danger">*</span>
                  </Label>
                  <Select
                    isSearchable
                    isClearable
                    placeholder={isLoadingContractTypes ? "Loading contract types..." : "Select contract type..."}
                    options={contractTypeOptions}
                    value={selectedContractTypeOption}
                    onChange={(option) => {
                      setSelectedContractTypeOption(option);
                      handleContractFormChange("contract_type", option?.value || "");
                    }}
                    isLoading={isLoadingContractTypes}
                    isDisabled={isSavingContract || isLoadingContractTypes}
                    noOptionsMessage={() => "No contract types found"}
                    styles={{
                      control: (base) => ({ ...base, borderRadius: "8px", minHeight: "38px" }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Contracted Salary <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={contractForm.contracted_salary}
                        onChange={(e) => handleContractFormChange("contracted_salary", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Total Gratuity Taken</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={contractForm.total_gratuity_taken}
                        onChange={(e) => handleContractFormChange("total_gratuity_taken", e.target.value)}
                        disabled={isSavingContract}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Annual Leave Days <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={contractForm.annual_leave_days}
                        onChange={(e) => handleContractFormChange("annual_leave_days", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <FormGroup>
                  <Label>Staff Code</Label>
                  <Input value={contractForm.staff_code} readOnly disabled />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Start Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={contractForm.start_date}
                        onChange={(e) => handleContractFormChange("start_date", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        End Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={contractForm.end_date}
                        onChange={(e) => handleContractFormChange("end_date", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Contracted Salary <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={contractForm.contracted_salary}
                        onChange={(e) => handleContractFormChange("contracted_salary", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        Annual Leave Days <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={contractForm.annual_leave_days}
                        onChange={(e) => handleContractFormChange("annual_leave_days", e.target.value)}
                        disabled={isSavingContract}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={closeContractFormModal} disabled={isSavingContract}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSavingContract}>
              {isSavingContract ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : contractFormMode === "create" ? (
                "Create Contract"
              ) : (
                "Update Contract"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default ContractTrackSheet;
